import { PaymentsProp } from '@wetags/payments';
import { PaymentsDeprecatedOrg, PaymentsDeprecatedProp } from '@wetags/payments-deprecated';
import { useMerchants } from '@frontend/payments-hooks';

/**
 * Care Credit (CC) & Payments Possible States
 *
 * Controlled by wetags: `care-credit-enabled` and `care-credit-decoupled`.
 *
 * | # | Payments | Care Credit | Active WE Tag           | Output Flags                                 | Context / Notes                  |
 * |:-:|:--------:|:-----------:|:-----------------------|:----------------------------------------------|:---------------------------------|
 * | 1 |    ON    |     OFF     | *(none)*               | `hasPayments: true`,  `showCareCredit: false` | Current DCA behavior             |
 * | 2 |    ON    |     ON      | `care-credit-enabled`  | `hasPayments: true`,  `showCareCredit: true`  | Most payments-enabled locations  |
 * | 3 |   OFF    |     ON      | `care-credit-decoupled`| `hasPayments: false`, `showCareCredit: true`  | Decoupled CC only                |
 * | 4 |   OFF    |     OFF     | *(none)*               | `hasPayments: false`, `showCareCredit: false` | All payment features disabled    |
 */

export const useCareCreditLocationStatus = (locationId: string | undefined) => {
  const [isCareCreditDecoupledWetagEnabled, isCareCreditDecoupledLoading] = PaymentsProp.useWetag(
    'care-credit-decoupled',
    locationId
  );

  const [isCareCreditWetagEnabled, isCareCreditLoading] = PaymentsDeprecatedProp.useWetag(
    'care-credit-enabled',
    locationId
  );
  const { isEnabled: isCareCreditOrgEnabled } = PaymentsDeprecatedOrg.useWetagQuery('care-credit-enabled');

  const { firstLocation } = useMerchants(locationId);
  const { hasPayments } = firstLocation;
  const showCareCredit = isCareCreditWetagEnabled || isCareCreditDecoupledWetagEnabled;
  const isCareCreditEnabled = isCareCreditWetagEnabled || isCareCreditOrgEnabled || false;
  const isCareCreditDecoupled = isCareCreditDecoupledWetagEnabled || false;

  return {
    isCareCreditOrgEnabled,
    isCareCreditWetagEnabled: isCareCreditEnabled,
    // Alias for backward compatibility with tests and consumers
    isCareCreditEnabled,
    isCareCreditLoading: isCareCreditLoading,
    // Decoupled care credit indicates that a location has the capability
    // to connect their care credit MID and use it to generate pURLs
    // for patients and accept payments via CC, without having a stripe
    // merchant connection.
    //
    // This value is used explicitly when passing the isDecoupled
    // argument to the useCareCreditPurlQuery so that the BE can know
    // whether or not it should track this payment as a decoupled payment
    isCareCreditDecoupledWetagEnabled: isCareCreditDecoupled,
    // Alias for backward compatibility with tests and consumers
    isCareCreditDecoupled,
    isCareCreditDecoupledLoading: isCareCreditDecoupledLoading,
    // A special flag to indicate the app should show CC UI.
    // Adding the flag here avoids doing the `||` in a lot
    // of component files.
    showCareCredit,
    hasPayments,
    // a convenience var to not require the caller to
    // think about how to derive this condition. the
    // difference between showDecoupled and isCareCreditDecoupledWetagEnabled
    // is that showDecoupled confirms that payments is also disabled, in
    // addition to `care-credit-decoupled` being enabled.
    showDecoupled: !hasPayments && showCareCredit,
    // Alias for backward compatibility with tests
    isDecoupled: !hasPayments && showCareCredit,
  };
};
